<template>
  <div class="flex flex-col">
    <div class="flex flex-col max-h-[300px]">
      <div v-if="tabs && tabs.length > 0" class="flex flex-col gap-2 px-2 pt-2">
        <div class="flex flex-row gap-1">
          <div
            v-for="(tab, index) in tabs"
            :key="`tab_${index}`"
            :class="[
              'list_category px-2 py-1 font-semibold rounded-lg cursor-pointer text-sm transition-all duration-300 ease-in-out',
              { active: activeTabIndex === index },
            ]"
            @click="setActiveTab(index)"
          >
            {{ tab }}
          </div>
        </div>
      </div>

      <div
        class="flex flex-col py-3 px-2"
        :class="{ border_bottom: currentFilteredOptions.length }"
      >
        <span
          v-if="!tabs || tabs.length === 0"
          class="secondary_color font-semibold text-xs leading-[17px] px-3 pb-2"
        >
          {{ computedSearchTitle.toUpperCase() }}
        </span>
        <a-input
          v-model:value="searchQuery"
          :placeholder="searchPlaceholder"
          class="input_medium"
          allow-clear
        >
          <template #prefix>
            <ThemeImage
              :icon="imageNames.SEARCH_VARIANT1"
              alt="search_icon"
              class="size-4"
            />
          </template>
        </a-input>
      </div>

      <div
        v-if="currentFilteredOptions.length"
        class="flex flex-col p-2 overflow-y-scroll max-h-[200px]"
      >
        <div
          v-for="(option, index) in currentFilteredOptions"
          :key="`${getOptionValue(option)}_option_${index}`"
          :class="getOptionClasses(option)"
          @click="toggleSelection(getOptionValue(option))"
        >
          <div
            class="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"
          >
            <a-checkbox
              :checked="isOptionSelected(getOptionValue(option))"
              :disabled="isSelectedAndNonRemovable(getOptionValue(option))"
              @change="(e) => handleCheckboxChange(e, getOptionValue(option))"
              @click.stop
            />
          </div>
          <div class="flex items-center flex-1 justify-between">
            <span class="text_color text-sm font-normal leading-[17px]">
              {{ getOptionLabel(option) }}
              <span
                v-if="isSelectedAndNonRemovable(getOptionValue(option))"
                class="text-xs secondary_color ml-1"
              >
                (You)
              </span>
            </span>
          </div>
        </div>
      </div>

      <div v-else class="flex flex-col items-center justify-center py-6 px-4">
        <ThemeImage
          :icon="imageNames.EMPTY_SCREEN_GREY"
          alt="empty_icon"
          class="size-16"
        />
        <span class="text-sm secondary_color text-center mt-2">
          No results found!
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  getOptionValueCommon,
  getOptionLabelCommon,
} from "@/src/utils/optionUtils";

const props = defineProps({
  tabs: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  modelValue: { type: Array, default: () => [] },
  searchTitle: { type: String, default: "Choose options" },
  searchPlaceholder: { type: String, default: "Search here" },
  nonRemovableItems: { type: Array, default: () => [] },
  enableAutoSelectCurrentUser: { type: Boolean, default: true },
  initialSelectionByTab: {
    type: Object,
    default: () => ({ users: [], teams: [] }),
  },
});

const emit = defineEmits([
  "update:modelValue",
  "selection-change",
  "tab-change",
]);

const searchQuery = ref("");
const activeTabIndex = ref(0);
const isInternalUpdate = ref(false);

const selectedValues = ref(
  props.tabs?.length > 0 ? props.tabs.map(() => []) : [...props.modelValue],
);

const hasTabs = computed(() => props.tabs?.length > 0);

const applyInitialSelectionByTab = (byTab) => {
  if (!hasTabs.value || !byTab) return;

  const usersTabIndex = props.tabs.findIndex(
    (tab) => tab.toLowerCase() === "users",
  );
  const teamsTabIndex = props.tabs.findIndex(
    (tab) => tab.toLowerCase() === "teams",
  );

  const nextSelections = props.tabs.map(() => []);

  if (usersTabIndex !== -1 && Array.isArray(byTab.users)) {
    nextSelections[usersTabIndex] = [...byTab.users];
  }
  if (teamsTabIndex !== -1 && Array.isArray(byTab.teams)) {
    nextSelections[teamsTabIndex] = [...byTab.teams];
  }

  selectedValues.value = nextSelections;
  const flattened = nextSelections.flat();
  isInternalUpdate.value = true;
  emit("update:modelValue", flattened);
  emit("selection-change", { flatSelections: flattened, byTab: byTab });
};

const currentOptions = computed(() => {
  if (!hasTabs.value) return props.options;

  if (Array.isArray(props.options[0])) {
    return props.options[activeTabIndex.value] || [];
  }
  return props.options;
});

const getCurrentTabSelectedValues = computed(() => {
  return hasTabs.value
    ? selectedValues.value[activeTabIndex.value] || []
    : selectedValues.value;
});

const computedSearchTitle = computed(() => {
  return hasTabs.value
    ? `Choose ${props.tabs[activeTabIndex.value]?.toLowerCase()}`
    : props.searchTitle;
});

const currentFilteredOptions = computed(() => {
  if (!searchQuery.value) return currentOptions.value;

  const query = searchQuery.value.toLowerCase();
  return currentOptions.value.filter((option) => {
    const label = String(getOptionLabel(option) ?? "").toLowerCase();
    const value = String(getOptionValue(option) ?? "").toLowerCase();
    return label.includes(query) || value.includes(query);
  });
});

const getOptionValue = (option) => getOptionValueCommon(option);
const getOptionLabel = (option) => getOptionLabelCommon(option);

const isOptionSelected = (value) => {
  const selections = getCurrentTabSelectedValues.value;
  return selections.some((v) => String(v) === String(value));
};

const isNonRemovable = (value) => {
  if (hasTabs.value) {
    const usersTabIndex = props.tabs.findIndex(
      (tab) => tab.toLowerCase() === "users",
    );
    if (activeTabIndex.value === usersTabIndex) {
      return props.nonRemovableItems.some(
        (item) => String(item) === String(value),
      );
    }
  }
  return false;
};

const isSelectedAndNonRemovable = (value) => {
  return isOptionSelected(value) && isNonRemovable(value);
};

const getOptionClasses = (option) => {
  const value = getOptionValue(option);
  const isSelected = isOptionSelected(value);
  const isNonRemovableItem = isSelectedAndNonRemovable(value);

  return [
    "dropdown_item flex-shrink-0 flex items-center flex-row gap-2 px-3 py-2 rounded-lg",
    {
      dropdown_item_selected: isSelected,
      "cursor-pointer": !isNonRemovableItem,
      "cursor-not-allowed opacity-60": isNonRemovableItem,
    },
  ];
};

const emitSelectionChange = () => {
  const flattenedSelections = selectedValues.value.flat();
  isInternalUpdate.value = true;
  emit("update:modelValue", flattenedSelections);

  if (hasTabs.value) {
    const usersTabIndex = props.tabs.findIndex(
      (tab) => tab.toLowerCase() === "users",
    );
    const teamsTabIndex = props.tabs.findIndex(
      (tab) => tab.toLowerCase() === "teams",
    );

    emit("selection-change", {
      flatSelections: flattenedSelections,
      byTab: {
        users: selectedValues.value[usersTabIndex] || [],
        teams: selectedValues.value[teamsTabIndex] || [],
      },
    });
  } else {
    emit("selection-change", {
      flatSelections: [...selectedValues.value],
      byTab: {},
    });
  }
};

const toggleSelection = (value) => {
  if (isSelectedAndNonRemovable(value)) return;

  if (hasTabs.value) {
    const currentTabSelections = [...getCurrentTabSelectedValues.value];
    const index = currentTabSelections.findIndex(
      (v) => String(v) === String(value),
    );

    if (index === -1) {
      currentTabSelections.push(value);
    } else {
      currentTabSelections.splice(index, 1);
    }

    selectedValues.value[activeTabIndex.value] = currentTabSelections;
  } else {
    const index = selectedValues.value.findIndex(
      (v) => String(v) === String(value),
    );
    if (index === -1) {
      selectedValues.value.push(value);
    } else {
      selectedValues.value.splice(index, 1);
    }
  }

  emitSelectionChange();
};

const handleCheckboxChange = (e, value) => {
  toggleSelection(value);
};

const setActiveTab = (index) => {
  activeTabIndex.value = index;
  searchQuery.value = "";
  emit("tab-change", { tabIndex: index, tabName: props.tabs[index] });
};

const redistributeSelections = (modelValueArray) => {
  if (!hasTabs.value) {
    selectedValues.value = [...modelValueArray];
    return;
  }

  const newSelections = props.tabs.map(() => []);

  modelValueArray.forEach((value) => {
    for (let tabIndex = 0; tabIndex < props.options.length; tabIndex++) {
      const tabOptions = Array.isArray(props.options[tabIndex])
        ? props.options[tabIndex]
        : props.options;

      if (Array.isArray(tabOptions)) {
        const foundOption = tabOptions.find(
          (option) => String(getOptionValue(option)) === String(value),
        );
        if (foundOption) {
          newSelections[tabIndex].push(value);
          break;
        }
      }
    }
  });

  selectedValues.value = newSelections;
};

const autoSelectCurrentUser = () => {
  if (!props.enableAutoSelectCurrentUser) return;

  const currentUserId = localStorage.getItem("userId");
  if (!currentUserId || !hasTabs.value) return;

  const flattenedSelections = selectedValues.value.flat();
  if (flattenedSelections.length > 0) return;

  const currentUserIdNum = parseInt(currentUserId);
  const usersTabIndex = props.tabs.findIndex(
    (tab) => tab.toLowerCase() === "users",
  );

  if (usersTabIndex !== -1 && activeTabIndex.value === usersTabIndex) {
    const allSelected = selectedValues.value.flat();
    if (!allSelected.some((id) => String(id) === String(currentUserIdNum))) {
      selectedValues.value[usersTabIndex] = [
        ...(selectedValues.value[usersTabIndex] || []),
        currentUserIdNum,
      ];
      emitSelectionChange();
    }
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (isInternalUpdate.value) {
      isInternalUpdate.value = false;
      return;
    }
    redistributeSelections(Array.isArray(newValue) ? newValue : []);
  },
  { deep: true },
);

watch(
  [() => props.tabs, () => props.options],
  () => {
    if (hasTabs.value) {
      if (
        props.initialSelectionByTab &&
        (props.initialSelectionByTab.users?.length ||
          props.initialSelectionByTab.teams?.length)
      ) {
        applyInitialSelectionByTab(props.initialSelectionByTab);
      } else {
        redistributeSelections(props.modelValue || []);
      }
    }
  },
  { deep: true },
);

watch(
  () => props.initialSelectionByTab,
  (byTab) => {
    if (hasTabs.value && (byTab?.users?.length || byTab?.teams?.length)) {
      applyInitialSelectionByTab(byTab);
    }
  },
  { deep: true },
);

watch(activeTabIndex, autoSelectCurrentUser);

onMounted(() => {
  if (
    hasTabs.value &&
    (props.initialSelectionByTab?.users?.length ||
      props.initialSelectionByTab?.teams?.length)
  ) {
    applyInitialSelectionByTab(props.initialSelectionByTab);
  } else {
    autoSelectCurrentUser();
  }
});
</script>

<style scoped>
.list_category {
  color: var(--secondary-color);
  line-height: 17px;
  white-space: nowrap;
}

.list_category:hover {
  background-color: var(--white-opacity-6);
}

.list_category.active {
  background-color: var(--white-opacity-6);
  color: var(--text-color);
}

.dropdown_item:hover {
  background-color: var(--white-opacity-10);
}

.dropdown_item_selected {
  background-color: var(--white-opacity-10);
}
</style>
